import React, { Component, lazy, Suspense } from 'react';
import { connect } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import { loadReCaptcha } from 'react-recaptcha-v3';
import { OptimizelyProvider } from '@optimizely/react-sdk';
import getOptimizelyInstance from 'JS/shared/helpers/optmizely';
import LoadingSpinner from 'JS/shared/components/LoadingSpinner';
import { usersnapFeedback } from 'JS/shared/helpers/usersnap';
import { appServices } from 'JS/shared/helpers/app-services/AppServices';

import Callout from './connectors/CalloutConnector';
import AuthenticatedComponent from './shared/components/AuthenticatedComponent';
import * as tokenHelper from './shared/helpers/TokenHelper';
import { setRecaptchaReady, validateToken, setAccountManagementRedirect } from './actionCreators/authActions';
import { ROUTE_CODES } from './config/routes';
import config from './config/config';
import loadFacebookPixel from './shared/helpers/loadFacebookPixel';
import { getStore } from './store';

import './App.css';
import '../css/modules/AuthForm.css';

const HomeView = lazy(() => import('./connectors/HomeViewConnector'));
const AuthenticationRouter = lazy(() => import('./authentication/AuthenticationRouter'));
const RendererView = lazy(() => import('./connectors/RendererViewConnector'));
const EditorView = lazy(() => import('./editor/EditorView'));
const ResetPasswordView = lazy(() => import('./connectors/ResetPasswordViewConnector'));
const ZuoraCallback = lazy(() => import('./shared/components/ZuoraCallback'));
const ContentfulCallback = lazy(() => import('./shared/components/ContentfulCallback'));

const { RECAPTCHA_SITEKEY, ENVIRONMENT } = config;

const optimizely = getOptimizelyInstance();

class App extends Component {
  componentDidMount() {
    const { validateToken, setRecaptchaReady, history, setAccountManagementRedirect } = this.props;

    if (!history.location.pathname.includes('/render')) {
      // Only load these dependencies for the main app and not the renderer to try and speed things up a little on the renderer.
      loadFacebookPixel();
      loadReCaptcha(RECAPTCHA_SITEKEY, () => setRecaptchaReady(true));
      usersnapFeedback(config.USERSNAP_KEY, getStore());
    }

    if (window.location.search.indexOf('referrer=account') !== -1) {
      setAccountManagementRedirect(true);
    }

    const token = tokenHelper.getSparkolToken();
    validateToken(token);
  }

  componentWillUnmount() {
    appServices.clearAssetCache();
  }

  render() {
    const { currentUser, isAuthenticating } = this.props;
    const { id, accountType, email } = currentUser;

    if (isAuthenticating) {
      return <LoadingSpinner />;
    }

    return (
      <OptimizelyProvider
        optimizely={optimizely}
        user={{ id, attributes: { userTier: accountType, env: ENVIRONMENT, email, userId: id } }}
      >
        <div className="App">
          <Callout />
          <Suspense fallback={<LoadingSpinner />}>
            <Switch>
              <Route path={ROUTE_CODES.AUTH} component={AuthenticationRouter} />
              <Route path={ROUTE_CODES.RENDER} component={RendererView} />
              <Route path={ROUTE_CODES.RESET_PASSWORD} component={ResetPasswordView} />
              <AuthenticatedComponent path={ROUTE_CODES.ZUORA_CALLBACK} component={ZuoraCallback} />
              <AuthenticatedComponent path={ROUTE_CODES.CONTENTFUL_CALLBACK} component={ContentfulCallback} />
              <AuthenticatedComponent path={ROUTE_CODES.EDITOR} component={EditorView} />
              <AuthenticatedComponent path={ROUTE_CODES.HOME} component={HomeView} />
            </Switch>
          </Suspense>
        </div>
      </OptimizelyProvider>
    );
  }
}

const mapStateToProps = ({ auth, login, ui, location }) => ({
  isAuthenticating: auth && auth.isAuthenticating,
  currentUser: auth && auth.currentUser
});

const mapDispatchToProps = dispatch => ({
  validateToken: token => dispatch(validateToken(token)),
  setRecaptchaReady: isReady => dispatch(setRecaptchaReady(isReady)),
  setAccountManagementRedirect: redirect => dispatch(setAccountManagementRedirect(redirect))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(App);
